/* eslint-disable max-len */
import PropTypes from 'prop-types';
import React from 'react';
import clsx from 'clsx';
import { Disc } from 'react-feather';
import Icon, { IconColors, IconSizes } from '../icon';
import './stepper.scss';
import CheckIcon from '../../icons/check-icon';

const StepperTypes = {
  FILLED: 'filled',
  UNFILLED: 'unfilled',
};
const StepperThemes = {
  LIGHT: 'light',
  MID: 'mid',
  DARK: 'dark',
};
const StepperOrientation = {
  VERTICAL: 'vertical',
  HORIZONTAL: 'horizontal',
};

const StepperSizes = {
  EXTRASMALL: 'size-xs',
  SMALL: 'size-s',
  MEDIUM: 'size-m',
};

const getClassName = (stepDetails, size, showLabelInline) => {
  let className = 'stepper-item ';
  const { isActive } = stepDetails;
  const { isCompleted } = stepDetails;
  if (isCompleted) {
    className += 'completed ';
  } else if (isActive) {
    className += 'active ';
  }
  if (showLabelInline) {
    className += 'inline-label ';
  }
  if (size === StepperSizes.MEDIUM) {
    className += 'size-m';
  } else if (size === StepperSizes.SMALL) {
    className += 'size-s';
  } else {
    className += 'size-xs';
  }
  return className;
};

const getIconSizeAndTypeClassName = (theme, size) => {
  let className = 'step-counter ';
  if (size === StepperSizes.MEDIUM) {
    className += 'size-m ';
  } else if (size === StepperSizes.SMALL) {
    className += 'size-s ';
  } else {
    className += 'size-xs ';
  }

  if (theme === StepperThemes.DARK) {
    className += 'dark-theme';
  } else if (theme === StepperThemes.MID) {
    className += 'mid-theme';
  } else {
    className += 'light-theme';
  }

  return className;
};

const getLabelAndHelper = ({
  label, helperText,
}) => (
  <div className="step-name-wrapper">
    <div className="step-name">
      {label}
    </div>
    <div className="helper-text">
      {helperText}
    </div>
  </div>
);
const getSizeClass = (size) => {
  if (size === StepperSizes.MEDIUM) {
    return 'size-m';
  } if (size === StepperSizes.SMALL) {
    return 'size-s';
  }
  return 'size-xs';
};

const getIconSize = (size) => {
  if (size === StepperSizes.MEDIUM) {
    return IconSizes.MEDIUM;
  } if (size === StepperSizes.SMALL) {
    return IconSizes.SMALL;
  }
  return IconSizes.X_SMALL;
};
const getIconColor = (theme) => {
  if (theme === StepperThemes.DARK) {
    return IconColors.LIGHT;
  } if (theme === StepperThemes.MID) {
    return IconColors.PRIMARY;
  }
  return IconColors.PRIMARY;
};
const getSecondaryIconColorForActive = (theme) => {
  if (theme === StepperThemes.DARK) {
    return IconColors.LIGHT;
  } if (theme === StepperThemes.MID) {
    return IconColors.PRIMARY;
  }
  return IconColors.PRIMARY;
};
const getSecondaryIconColorForNonActive = (theme) => {
  if (theme === StepperThemes.DARK) {
    return IconColors.LIGHT;
  } if (theme === StepperThemes.MID) {
    return IconColors.GREY_200;
  }
  return IconColors.GREY_200;
};
const getSteps = (stepDetails, theme, size, showLabelInline) => {
  if (stepDetails.isCompleted) {
    return (
      <>
        <div className={`step-counter-wrapper ${getSizeClass(size)}`}>
          <div className={getIconSizeAndTypeClassName(theme, size)}>
            <Icon
              icon={CheckIcon}
              color={getIconColor(theme)}
              size={getIconSize(size)}
            />
          </div>
          {showLabelInline && getLabelAndHelper(stepDetails)}
        </div>
        {!showLabelInline && getLabelAndHelper(stepDetails)}
      </>
    );
  }
  return (
    <>
      {!stepDetails.isActive && !stepDetails.isCompleted
        ? (
          <div className={`step-counter-wrapper ${getSizeClass(size)}`}>
            <div className={getIconSizeAndTypeClassName(theme, size)}>
              <Icon icon={Disc} color={`${getSecondaryIconColorForNonActive(theme)}`} size={IconSizes.LARGE} className="stepper-icon" />
            </div>
            {showLabelInline && getLabelAndHelper(stepDetails)}
          </div>
        )
        : (
          <div className={`step-counter-wrapper ${getSizeClass(size)}`}>
            <div className={getIconSizeAndTypeClassName(theme, size)}>
              <Icon icon={Disc} color={`${getSecondaryIconColorForActive(theme)}`} size={IconSizes.LARGE} className="stepper-icon" />
            </div>
            {showLabelInline && getLabelAndHelper(stepDetails)}
          </div>
        )}
      {!showLabelInline && getLabelAndHelper(stepDetails)}
    </>
  );
};

const getStepContent = (stepDetails, theme, size, showLabelInline) => (
  <div className={getClassName(stepDetails, size, showLabelInline)} key={stepDetails.id}>
    {getSteps(stepDetails, theme, size, showLabelInline)}
  </div>
);

const getVersion = (items, type, theme, orientation, size, showLabelInline) => {
  let effectiveTheme;
  if (theme) {
    effectiveTheme = theme;
  } else if (type === StepperTypes.FILLED) {
    effectiveTheme = StepperThemes.DARK;
  } else if (type === StepperTypes.UNFILLED) {
    effectiveTheme = StepperThemes.MID;
  } else {
    effectiveTheme = null;
  }

  return (
    <div className={`${orientation}-wrapper`}>
      {items.map((step) => getStepContent(step, effectiveTheme, size, showLabelInline))}
    </div>
  );
};

const Stepper = ({
  items, type, theme, orientation, size, showLabelInline, className,
}) => (
  <div className={clsx('stepper-content', { [className]: className })}>
    {getVersion(items, type, theme, orientation, size, showLabelInline)}
  </div>
);

Stepper.defaultProps = {
  items: [],
  type: StepperTypes.FILLED,
  theme: null,
  orientation: StepperOrientation.HORIZONTAL,
  size: StepperSizes.MEDIUM,
  showLabelInline: false,
  className: '',
};

Stepper.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
    isActive: PropTypes.bool,
    isCompleted: PropTypes.bool,
    helperText: PropTypes.string,
  })),
  type: PropTypes.oneOf(Object.values(StepperTypes)),
  theme: PropTypes.oneOf(Object.values(StepperThemes)),
  orientation: PropTypes.oneOf(Object.values(StepperOrientation)),
  size: PropTypes.oneOf(Object.values(StepperSizes)),
  showLabelInline: PropTypes.bool,
  className: PropTypes.string,
};

export {
  Stepper as default,
  StepperTypes,
  StepperThemes,
  StepperOrientation,
  StepperSizes,
};
